import { PageLayout } from '../components/Layout';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const TermsPage = () => {
  useEffect(() => {
    document.title = 'Terms and Conditions | TryWeb';
  }, []);

  return (
    <PageLayout>
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-8">Terms and Conditions</h1>
          <p className="text-muted-foreground mb-8">Last updated: February 2026</p>

          <div className="prose prose-lg prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms and Conditions govern your use of the website design and development services provided by TryWeb ("we", "us", "our"). By placing an order or using our services, you agree to be bound by these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">2. Services</h2>
              <p className="text-muted-foreground leading-relaxed">
                TryWeb provides professional website design and development services for businesses. Our services include custom website creation, hosting, domain registration, and ongoing maintenance as specified in your chosen package.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">3. Pricing and Payment Plans</h2>
              
              <h3 className="text-xl font-semibold text-foreground mb-3">3.1 Monthly Subscription Plan (£59.99/month)</h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Minimum Term:</strong> This plan requires a minimum commitment of 12 months. By subscribing, you agree to pay the monthly fee for at least 12 consecutive months.</li>
                <li><strong>Early Termination:</strong> If you wish to cancel before the 12-month minimum term, you will be required to pay the remaining balance of the minimum term.</li>
                <li><strong>After Minimum Term:</strong> Following the initial 12-month period, the subscription continues on a month-to-month basis and can be cancelled with 30 days written notice.</li>
                <li><strong>What&apos;s Included:</strong> Website design, hosting, SSL certificate, premium domain, and ongoing support.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">3.2 Setup + Hosting Plan (£500 + £24.99/month)</h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Setup Fee:</strong> A one-time setup fee of £500 is payable upon order.</li>
                <li><strong>Monthly Hosting:</strong> After setup, monthly hosting fees of £24.99 apply.</li>
                <li><strong>Cancellation:</strong> Hosting can be cancelled with 30 days written notice. Setup fees are non-refundable.</li>
                <li><strong>What&apos;s Included:</strong> Website design, hosting, SSL certificate, premium domain, and ongoing support.</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">3.3 Full Purchase Plan (£1,399.99)</h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>One-Time Payment:</strong> A single payment of £1,399.99 grants you full ownership of the website.</li>
                <li><strong>Ownership:</strong> You receive complete source code and can host the website anywhere of your choosing.</li>
                <li><strong>No Ongoing Fees:</strong> There are no monthly fees with this plan.</li>
                <li><strong>Optional Hosting:</strong> If you choose TryWeb hosting after purchase, this will be subject to separate hosting terms.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">4. Payment Terms</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>All payments are processed securely through Stripe.</li>
                <li>Prices are in GBP (British Pounds) and include VAT where applicable.</li>
                <li>For subscription plans, payment will be taken automatically on the same date each month.</li>
                <li>Failed payments will result in a 7-day grace period, after which services may be suspended.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Refund Policy</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Monthly Subscription:</strong> No refunds for partial months. Refunds for the minimum term early termination are not available.</li>
                <li><strong>Setup Fee:</strong> The £500 setup fee is non-refundable once work has commenced.</li>
                <li><strong>Full Purchase:</strong> Refunds are available within 14 days of purchase if no customizations have been requested and work has not begun.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Website Content</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>You are responsible for providing accurate business information for your website.</li>
                <li>We reserve the right to refuse content that is illegal, offensive, or violates third-party rights.</li>
                <li>Stock images used may be subject to licensing terms from their respective providers.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Domain Names</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Premium domains are included with all packages and remain active while your service is active.</li>
                <li>For Full Purchase customers, domain transfer to your own registrar is available upon request.</li>
                <li>Domain availability cannot be guaranteed until registration is complete.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Intellectual Property</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Monthly/Setup Plans:</strong> TryWeb retains ownership of the website code and design. You are granted a license to use the website while your subscription is active.</li>
                <li><strong>Full Purchase:</strong> Full intellectual property rights transfer to you upon final payment.</li>
                <li>Content you provide (logos, text, images) remains your property.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">9. Service Level</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>We aim for 99.9% website uptime for hosted websites.</li>
                <li>Scheduled maintenance will be communicated in advance where possible.</li>
                <li>Support is available via email during business hours (Monday-Friday, 9am-5pm GMT).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">10. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                TryWeb shall not be liable for any indirect, incidental, or consequential damages arising from your use of our services. Our total liability shall not exceed the amount paid by you in the 12 months preceding any claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">11. Termination</h2>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>We may terminate services for breach of these terms, non-payment, or illegal activity.</li>
                <li>Upon termination, hosted websites will be taken offline and data may be deleted after 30 days.</li>
                <li>Full Purchase customers who self-host are not affected by termination of our services.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">12. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these terms at any time. Changes will be effective upon posting to our website. Continued use of our services constitutes acceptance of any modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">13. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">14. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms and Conditions, please contact us at{' '}
                <a href="mailto:andrew@tryweb.co.uk" className="text-primary hover:underline">andrew@tryweb.co.uk</a>
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/privacy" className="text-primary hover:underline">
              View our Privacy Policy →
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default TermsPage;
